import React, { ReactElement, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useAuthenticator } from '@aws-amplify/ui-react-native';
import { DNABox, DNAButton, Iffy, luxColors } from '@alucio/lux-ui';
import FormWrapper from '../../FormWrapper';
import InputComponent from 'src/components/Publishers/InputComponent';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import withPasswordChecker, { ValidationType } from '../PasswordChecker';
import * as logger from 'src/utils/logger'
import PasswordCriterias from '../PasswordCriterias';
import useSSO from 'src/components/DNA/hooks/useSso';
import { useLocation } from 'src/router';
import {
  AuthenticationCopy,
  getAuthenticationCopy,
  getAuthenticationLayout,
  getForgotPasswordMessageAlignment,
  shouldReserveForgotPasswordErrorSpace,
} from '../../authenticationLayout';

const styles = StyleSheet.create({
  changePasswordButton: {
    marginVertical: 24,
  },
  emailSentText: {
    color: luxColors.success.primary,
    fontSize: 13.5,
    marginTop: 16,
  },
  v2ChangePasswordButton: {
    marginBottom: 0,
    marginTop: 0,
  },
  v2ChangePasswordAction: {
    marginBottom: 16,
    marginTop: 12,
  },
  v2EmailSentText: {
    marginTop: 0,
  },
  v2MarginTop: {
    marginTop: 16,
  },
  enterEmailText: {
    color: luxColors.text.secondary,
    fontSize: 14,
    marginBottom: 25,
  },
  errorWrapper: {
    height: 20,
    marginTop: 10,
    marginBottom: 10,
  },
  invalidMessage: {
    color: luxColors.error.primary,
    fontSize: 12,
  },
  mainWrapper: {
    flexDirection: 'column',
    width: '100%',
  },
  marginTop: {
    marginTop: 20,
  },
  passwordUpdatedText: {
    alignSelf: 'center',
    color: luxColors.success.primary,
    fontSize: 13,
    marginTop: 20,
    marginBottom: 20,
  },
  resendCode: {
    color: luxColors.thumbnailBorder.primary,
    fontSize: 13,
  },
  resendWrapper: {
    alignItems: 'flex-end',
    zIndex: 1,
  },
  return: {
    color: luxColors.thumbnailBorder.primary,
    fontSize: 13,
  },
  row: {
    flexDirection: 'row',
  },
  sendCodeButton: {
    marginTop: 10,
  },
  setPasswordText: {
    color: luxColors.alucioPurple.primary,
    fontSize: 24,
    fontWeight: 'bold',
    paddingBottom: 20,
    paddingTop: 20,
  },
});

const USER_NOT_FOUND = 'UserNotFoundException';
const INVALID_CODE = 'CodeMismatchException';

interface Props {
  isPasswordValid: boolean;
  onPasswordChange: (pswd: string) => void,
  onRepeatedPasswordChange: (pswd: string) => void,
  password: string;
  passwordValidations: ValidationType;
  repeatedPassword: string;
  setDisplayLoginButton: (show: boolean) => void;
  authenticationCopy: AuthenticationCopy;
}

interface EnterUsernameProps {
  setUsername: (username: string) => void;
  authenticationCopy: AuthenticationCopy;
}

const ForgotPassword = (props: Props) => {
  const {
    isPasswordValid,
    passwordValidations,
    onPasswordChange,
    onRepeatedPasswordChange,
    password,
    repeatedPassword,
    authenticationCopy,
  } = props;
  const [username, setUsername] = useState<string>('');
  const [disabled, setDisabled] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [unexpectedError, setUnexpectedError] = useState<boolean>(false);
  const [invalidCode, setInvalidCode] = useState<boolean>(false);
  const [passwordChanged, setPasswordChanged] = useState<boolean>(false);
  const [emailResent, setEmailResent] = useState<boolean>(false);
  const isSubmitDisabled = !isPasswordValid || disabled;
  const { toSignIn } = useAuthenticator();
  const location = useLocation();
  const shouldReserveErrorSpace = shouldReserveForgotPasswordErrorSpace(
    location.search,
    invalidCode || unexpectedError,
  );
  const isAuthenticationV2 = getAuthenticationLayout(location.search) === 'new';
  const confirmationMessageAlignment = getForgotPasswordMessageAlignment(location.search);

  async function onSendCode(): Promise<void> {
    setDisabled(true);
    logger.auth.signIn.forgotPassword.info('Sending password reset code');
    try {
      await resetPassword({
        username: username?.toLocaleLowerCase(),
      });
      setUnexpectedError(false);
      setEmailResent(true);
      setCode('');
      onPasswordChange('')
      onRepeatedPasswordChange('')
    } catch (e) {
      logger.auth.signIn.forgotPassword.error('Unexpected error happened within onSendCode function', e);
      setUnexpectedError(true);
      setInvalidCode(false);
    } finally {
      setDisabled(false);
    }
  }

  function onCodeChange(value: string): void {
    setCode(value.trim());
    logger.auth.signIn.forgotPassword.info('Code input changed');
  }

  async function changePassword(): Promise<void> {
    setDisabled(true);
    logger.auth.signIn.forgotPassword.info('Changing password');
    try {
      await confirmResetPassword({
        confirmationCode: code,
        username: username?.toLocaleLowerCase(),
        newPassword: password,
      });
      setPasswordChanged(true);

      analytics?.track('LOGIN_RESET_PASSWORD_SUCCESS', {
        action: 'RESET_PASSWORD_SUCCESS',
        category: 'LOGIN',
      });

      props.setDisplayLoginButton(false);
    } catch (e: any) {
      if (e.code === INVALID_CODE) {
        setInvalidCode(true);
        setUnexpectedError(false);
        logger.auth.signIn.forgotPassword.warn('Invalid verification code provided');
      } else {
        logger.auth.signIn.forgotPassword.error('Unexpected error happened within changePassword function', e);
        setInvalidCode(false);
        setUnexpectedError(true);
      }
      setDisabled(false);
    }
  }

  if (!username) {
    return (
      <EnterUsername
        authenticationCopy={authenticationCopy}
        setUsername={(username) => setUsername(username.toLocaleLowerCase())}
      />
    );
  }

  if (passwordChanged) {
    return <SuccessfulChangedPassword goToLogin={toSignIn} />
  }

  return (
    <View style={styles.mainWrapper}>
      <Iffy is={!emailResent}>
        <DNABox appearance="col" fill alignX={confirmationMessageAlignment}>
          <Text
            testID="confirmation-label"
            style={[
              styles.emailSentText,
              isAuthenticationV2 ? styles.v2EmailSentText : undefined,
            ]}
          >
            Email sent! Please enter your code below to reset password.
          </Text>
        </DNABox>
      </Iffy>
      <Iffy is={emailResent}>
        <DNABox appearance="col" fill alignX={confirmationMessageAlignment}>
          <Text
            style={[
              styles.emailSentText,
              isAuthenticationV2 ? styles.v2EmailSentText : undefined,
            ]}
          >
            Email resent! Please enter your new code below.
          </Text>
        </DNABox>
      </Iffy>
      <PasswordCriterias passwordValidations={passwordValidations} />
      <Iffy is={shouldReserveErrorSpace}>
        <View style={styles.errorWrapper}>
          <Iffy is={invalidCode}>
            <Text style={styles.invalidMessage}>
              {authenticationCopy.forgotPasswordInvalidVerificationWarning}
            </Text>
          </Iffy>
          <Iffy is={unexpectedError}>
            {authenticationCopy.forgotPasswordUnexpectedErrorMessages.map((message) => (
              <Text key={message} style={styles.invalidMessage}>{message}</Text>
            ))}
          </Iffy>
        </View>
      </Iffy>
      <View style={isAuthenticationV2 ? styles.v2MarginTop : styles.marginTop}>
        <View style={styles.resendWrapper}>
          <TouchableOpacity onPress={onSendCode} style={{ position: 'absolute' }}>
            <Text style={styles.resendCode}>Resend code</Text>
          </TouchableOpacity>
        </View>
        <InputComponent
          disabled={disabled}
          removeMarginPadding={true}
          required={false}
          title="CODE"
          placeHolder="Code"
          titleColor={luxColors.text.primary}
          value={code}
          onChangeText={onCodeChange}
        />
      </View>
      <View style={isAuthenticationV2 ? styles.v2MarginTop : styles.marginTop}>
        <InputComponent
          disabled={disabled}
          isPassword={true}
          removeMarginPadding={true}
          required={false}
          title="PASSWORD"
          placeHolder="Password"
          titleColor={luxColors.text.primary}
          value={password}
          onChangeText={onPasswordChange}
        />
      </View>
      <View style={isAuthenticationV2 ? styles.v2MarginTop : styles.marginTop}>
        <InputComponent
          disabled={disabled}
          isPassword={true}
          removeMarginPadding={true}
          required={false}
          title="CONFIRM PASSWORD"
          placeHolder="Confirm Password"
          titleColor={luxColors.text.primary}
          value={repeatedPassword}
          onChangeText={onRepeatedPasswordChange}
        />
      </View>
      <Iffy is={isAuthenticationV2}>
        <DNABox appearance="col" style={styles.v2ChangePasswordAction}>
          <DNAButton
            stretch
            size="xl"
            onPress={changePassword}
            disabled={isSubmitDisabled}
            children="Set password"
            style={styles.v2ChangePasswordButton}
          />
        </DNABox>
      </Iffy>
      <Iffy is={!isAuthenticationV2}>
        <DNAButton
          stretch
          onPress={changePassword}
          disabled={isSubmitDisabled}
          children="Set password"
          style={styles.changePasswordButton}
        />
      </Iffy>
    </View>
  );
};

function EnterUsername(props: EnterUsernameProps): ReactElement {
  const [username, setUsername] = useState<string>('');
  const [invalidEmail, setInvalidEmail] = useState<boolean>(false);
  const [unexpectedError, setUnexpectedError] = useState<boolean>(false);
  const [disabledSubmit, setDisabledSubmit] = useState<boolean>(false);
  const isSubmitDisabled = disabledSubmit || username.length === 0;

  const { getProviderForEmail, isSSoUser, isValidating } = useSSO();
  const { authenticationCopy } = props;
  const location = useLocation();
  const isAuthenticationV2 = getAuthenticationLayout(location.search) === 'new';

  async function onUsernameChange(email: string): Promise<void> {
    setUsername(email?.toLocaleLowerCase());
    logger.auth.signIn.forgotPassword.debug('Username input changed');
  }

  async function onSendCode(): Promise<void> {
    setInvalidEmail(false)
    setUnexpectedError(false)
    logger.auth.signIn.forgotPassword.info('Sending password reset code');
    const providerName = await getProviderForEmail(username?.toLocaleLowerCase());
    if (providerName) return;

    analytics?.track('LOGIN_RESET_PASSWORD_REQUEST', {
      action: 'RESET_PASSWORD_REQUEST',
      category: 'LOGIN',
    });

    setDisabledSubmit(true);
    try {
      await resetPassword({ username: username.trim() });
      props.setUsername(username?.toLocaleLowerCase()?.trim());
    } catch (e: any) {
      if (e.code === USER_NOT_FOUND) {
        setInvalidEmail(true);
        setUnexpectedError(false);
        logger.auth.signIn.forgotPassword.warn('User not found');
      } else {
        logger.auth.signIn.forgotPassword.error('Unexpected error happened within onSendCode function', e);
        setInvalidEmail(false);
        setUnexpectedError(true);
      }
      setDisabledSubmit(false);
    }
  }

  return (
    <View style={[styles.mainWrapper, { marginBottom: 20 }]}>
      <Text style={styles.enterEmailText}>Enter the email address associated with your account.</Text>
      <View style={styles.marginTop}>
        <InputComponent
          testID="email-login"
          removeMarginPadding={true}
          required={false}
          title={authenticationCopy.emailLabel}
          placeHolder={authenticationCopy.emailPlaceholder}
          titleColor={luxColors.text.primary}
          onSubmitEditing={isSubmitDisabled ? () => null : onSendCode}
          onChangeText={onUsernameChange}
        />
      </View>
      <View style={[styles.errorWrapper, { marginBottom: isSSoUser || unexpectedError ? 20 : 0 }, { margin: 0 }]}>
        <Iffy is={invalidEmail}>
          <Text style={styles.invalidMessage}>{authenticationCopy.forgotPasswordInvalidEmailWarning}</Text>
        </Iffy>
        <Iffy is={unexpectedError}>
          {authenticationCopy.forgotPasswordUnexpectedErrorMessages.map((message) => (
            <Text key={message} style={styles.invalidMessage}>{message}</Text>
          ))}
        </Iffy>
        <Iffy is={isSSoUser}>
          <Text style={styles.invalidMessage}>
            Please contact your organization system’s administrator to change your password.
          </Text>
        </Iffy>
      </View>
      <DNAButton
        testID="send-code"
        stretch
        size={isAuthenticationV2 ? 'xl' : undefined}
        onPress={onSendCode}
        disabled={isSubmitDisabled || (isSSoUser || isValidating)}
        children="Send code"
        style={styles.sendCodeButton}
      />
    </View>
  );
}

function SuccessfulChangedPassword(props): ReactElement {
  logger.auth.signIn.forgotPassword.info('Password successfully changed');
  return (
    <View style={[styles.mainWrapper, { marginBottom: 20 }]}>
      <Text style={styles.passwordUpdatedText}>Your password was successfully updated!</Text>
      <DNAButton onPress={props.goToLogin} children="Log in" style={styles.sendCodeButton}/>
    </View>
  );
}

const ForgotPasswordWrapper = (props) => {
  const [displayLoginButton, setDisplayLoginButton] = useState<boolean>(true);
  const location = useLocation();
  const authenticationCopy = getAuthenticationCopy(location.search);

  return (
    <FormWrapper
      resetPassword={getAuthenticationLayout(location.search) === 'new'}
      title={authenticationCopy.forgotPasswordTitle}
      showReturnToLogin={displayLoginButton}
      {...props}
    >
      <ForgotPassword
        {...props}
        authenticationCopy={authenticationCopy}
        setDisplayLoginButton={setDisplayLoginButton}
      />
    </FormWrapper>
  );
};

ForgotPasswordWrapper.displayName = 'ForgotPassword';

export default withPasswordChecker(ForgotPasswordWrapper);
